import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthRestAPI from "../../../restAPI/AuthRestAPI";
import moguLogo from "../../../assets/logo/mogu-logo-cover.png";
import AuthPageAside from "./AuthPageAside";
import "./AuthFlow.css";

const STRENGTH_LABELS = ["-", "Weak", "Fair", "Good", "Strong"];

const Brand = () => (
  <div className="brand">
    <span className="brand-mark brand-mark--login-cover">
      <img className="brand-mark-image-cover" src={moguLogo} alt="MOGU logo" />
    </span>
  </div>
);

function getRequirements(pw) {
  return {
    length: pw.length >= 10,
    upper: /[A-Z]/.test(pw),
    number: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

function scorePassword(pw) {
  if (!pw) return 0;
  const r = getRequirements(pw);
  let s = 0;
  if (r.length) s++;
  if (r.upper) s++;
  if (r.number) s++;
  if (r.symbol) s++;
  return s;
}

function readResetParams(searchParams) {
  const tokenFromParams = searchParams.get("token");
  const emailFromParams = searchParams.get("email");

  if (tokenFromParams) {
    return {
      token: tokenFromParams,
      email: emailFromParams || "",
    };
  }

  const rawSearch = window.location.search || "";
  if (!rawSearch.includes("?token=")) {
    return { token: "", email: "" };
  }

  const query = rawSearch.slice(1);
  const tokenStart = query.indexOf("token=");
  const emailStart = query.indexOf("?email=");

  if (tokenStart === -1) {
    return { token: "", email: "" };
  }

  const token = emailStart === -1
    ? query.slice(tokenStart + "token=".length)
    : query.slice(tokenStart + "token=".length, emailStart);
  const email = emailStart === -1
    ? ""
    : decodeURIComponent(query.slice(emailStart + "?email=".length));

  return { token, email };
}

const ChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [urlToken, setUrlToken] = useState("");
  const [email, setEmail] = useState("");

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirectIn, setRedirectIn] = useState(5);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const { token, email: resetEmail } = readResetParams(searchParams);
    setUrlToken(token);
    setEmail(resetEmail);
  }, [searchParams]);

  const reqs = useMemo(() => getRequirements(pw), [pw]);
  const score = useMemo(() => scorePassword(pw), [pw]);
  const allMet = reqs.length && reqs.upper && reqs.number && reqs.symbol;
  const matches = pw.length > 0 && pw === pw2;
  const canSubmit = allMet && matches && !submitting;
  const avatarLetter = (email || "A").charAt(0).toUpperCase();

  useEffect(() => {
    if (!success) return;
    if (redirectIn <= 0) {
      navigate("/login");
      return;
    }
    const t = setTimeout(() => setRedirectIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [success, redirectIn, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!urlToken) {
      setSubmitError("Invalid reset link. Please request a new password reset link.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const formData = { password: pw, passwordConfirm: pw2 };
      await AuthRestAPI.resetPassword(
        urlToken,
        localStorage.getItem("groupId"),
        localStorage.getItem("companyId"),
        formData
      );
      setSuccess(true);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          "The password reset link may be invalid or expired. Please request a new one."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell">
      <section className="left">
        <Brand />
        <div className="form-wrap">
          {!success ? (
            <>
              <div className="account-chip"><span className="avatar">{avatarLetter}</span>Resetting password for <strong>{email || "your account"}</strong></div>
              <div className="accent-bar"></div>
              <h1>Create a new <em>password</em></h1>
              <p className="subtitle">Choose a strong password you haven't used before.</p>
              {submitError && <div className="field-error" role="alert">{submitError}</div>}

              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label htmlFor="pw">New password</label>
                  <input id="pw" className="has-icon-right" type={showPw ? "text" : "password"} placeholder="Enter a new password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required />
                  <button type="button" className="toggle-eye" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((s) => !s)}>Show</button>
                  <div className="meter" data-score={score}>
                    <div className="meter-bar"><span className="meter-seg"></span><span className="meter-seg"></span><span className="meter-seg"></span><span className="meter-seg"></span></div>
                    <div className="meter-label"><span>Password strength</span><span>{STRENGTH_LABELS[score]}</span></div>
                  </div>
                  <div className="reqs">
                    <div>Must include</div>
                    <ul>
                      <li>At least 10 characters</li>
                      <li>One uppercase letter</li>
                      <li>One number</li>
                      <li>One symbol</li>
                    </ul>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="pw2">Confirm new password</label>
                  <input id="pw2" className="has-icon-right" type={showPw2 ? "text" : "password"} placeholder="Re-enter your new password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
                  <button type="button" className="toggle-eye" aria-label={showPw2 ? "Hide password" : "Show password"} onClick={() => setShowPw2((s) => !s)}>Show</button>
                  {pw2.length > 0 && <div className={`match-line ${matches ? "matched" : "mismatched"}`}>{matches ? "Passwords match" : "Passwords don't match yet"}</div>}
                </div>

                <button className="btn" type="submit" disabled={!canSubmit}>{submitting ? "Updating..." : "Update password"}</button>
              </form>
            </>
          ) : (
            <>
              <div className="success-icon"><svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg></div>
              <h1>Password <em>updated</em></h1>
              <p className="subtitle">Your password has been changed successfully.</p>
              <button type="button" className="btn" onClick={() => navigate("/login")}>Go to sign in</button>
              <div className="countdown-note">Redirecting to sign in in <strong>{redirectIn}</strong>s...</div>
            </>
          )}
          <div className="foot"><span>© {new Date().getFullYear()} MOGU Sdn Bhd</span></div>
        </div>
      </section>

      <AuthPageAside>
        <div className="right-eyebrow">Security - final step</div>
        <h2>Pick a password worth <em>protecting</em> your documents.</h2>
        <p className="lede">Your password guards every invoice and submission tied to your account.</p>
        <div className="panel">
          <div className="panel-title" style={{ marginBottom: 18 }}>Quick tips</div>
          <ul className="panel-list">
            <li><span className="ic">•</span><div><strong>Use a passphrase.</strong></div></li>
            <li><span className="ic">•</span><div><strong>Don't reuse passwords.</strong></div></li>
            <li><span className="ic">•</span><div><strong>Use a password manager.</strong></div></li>
          </ul>
        </div>
      </AuthPageAside>
    </div>
  );
};

export default ChangePassword;
